import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    redirect: () => {
      const storedUser = getStoredUser()
      return storedUser ? '/dashboard' : '/login'
    },
  },

  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/auth/LoginView.vue'),
    meta: { guestOnly: true },
  },
  {
    path: '/register',
    name: 'register',
    component: () => import('@/views/auth/RegisterView.vue'),
    meta: { guestOnly: true },
  },
  {
    path: '/forgot-password',
    name: 'forgot-password',
    component: () => import('@/views/auth/ForgotPasswordView.vue'),
    meta: { guestOnly: true },
  },
  {
    path: '/reset-password',
    name: 'reset-password',
    component: () => import('@/views/auth/ResetPasswordView.vue'),
    meta: { guestOnly: true },
  },

  {
    path: '/dashboard',
    name: 'dashboard',
    component: () => import('@/views/DashboardView.vue'),
    meta: { requiresAuth: true },
  },

  {
    path: '/settings',
    name: 'settings',
    component: () => import('@/views/SettingsView.vue'),
    meta: { requiresAuth: true },
  },

  {
    path: '/companies',
    name: 'companies',
    component: () => import('@/views/CompaniesView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/companies/create',
    name: 'companies-create',
    component: () => import('@/views/CreateCompanyView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/companies/suggestions',
    name: 'companies-suggestions',
    component: () => import('@/views/SuggestionsView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/companies/:id',
    name: 'company',
    component: () => import('@/views/CompanyView.vue'),
    meta: { requiresAuth: true },
  },

  {
    path: '/campaigns',
    name: 'campaigns',
    component: () => import('@/views/CampaignsView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/campaigns/:id',
    name: 'campaign',
    component: () => import('@/views/CampaignView.vue'),
    meta: { requiresAuth: true },
  },

  {
    path: '/campaign-programs',
    name: 'campaign-programs',
    component: () => import('@/views/CampaignProgramsView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/campaign-programs/:id',
    name: 'campaign-program',
    component: () => import('@/views/CampaignProgramView.vue'),
    meta: { requiresAuth: true },
  },

  {
    path: '/pipeline',
    name: 'pipeline',
    component: () => import('@/views/PipelineView.vue'),
    meta: { requiresAuth: true },
  },

  {
    path: '/outreach',
    name: 'outreach',
    component: () => import('@/views/OutreachView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/analytics',
    name: 'analytics',
    component: () => import('@/views/AnalyticsView.vue'),
    meta: { requiresAuth: true },
  },

  {
    path: '/activity',
    name: 'activity',
    component: () => import('@/views/ActivityLogView.vue'),
    meta: { requiresAuth: true },
  },

  {
    path: '/team',
    name: 'team',
    component: () => import('@/views/TeamView.vue'),
    meta: { requiresAuth: true },
  },

  {
    path: '/admin/ai-logs',
    name: 'admin-ai-logs',
    component: () => import('@/views/admin/AiLogsView.vue'),
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: '/admin/ai-logs/:id',
    name: 'admin-ai-log',
    component: () => import('@/views/admin/AiLogView.vue'),
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: '/admin/audit-log',
    name: 'admin-audit-log',
    component: () => import('@/views/admin/AuditLogView.vue'),
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: '/admin/unmatched-bounces',
    name: 'admin-unmatched-bounces',
    component: () => import('@/views/admin/UnmatchedBouncesView.vue'),
    meta: { requiresAuth: true, requiresAdmin: true },
  },

  {
    path: '/:pathMatch(.*)*',
    redirect: '/dashboard',
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
})

function getStoredUser() {
  try {
    const raw = localStorage.getItem('user')
    if (!raw || raw === 'undefined' || raw === 'null') return null
    return JSON.parse(raw)
  } catch {
    localStorage.removeItem('user')
    return null
  }
}

router.beforeEach(async (to, _from, next) => {
  const storedUser = getStoredUser()
  const isAuthenticated = storedUser !== null

  if (to.meta.guestOnly && isAuthenticated) {
    return next('/dashboard')
  }

  if (to.meta.requiresAuth && !isAuthenticated) {
    return next({ name: 'login', query: { redirect: to.fullPath } })
  }

  if (to.meta.requiresAuth && isAuthenticated) {
    const { useSessionStore } = await import('@/store/useSessionStore')
    const session = useSessionStore()

    if (!session.currentWorkspace) {
      await session.fetchSession()

      if (!session.currentWorkspace) {
        session.clearSession()
        return next({ name: 'login' })
      }
    }

    if (to.meta.requiresAdmin && !session.isAdmin) {
      return next({ name: 'dashboard' })
    }
  }

  next()
})

export default router
